"use client"

import confetti from "canvas-confetti"
import { useEffect, useRef } from "react"

interface ConfettiProps {
  /**
   * Number of particles to show (default: 200)
   */
  particleCount?: number
  /**
   * Whether to fire confetti on mount (default: true)
   */
  fireOnMount?: boolean
  /**
   * Callback after confetti animation completes
   */
  onComplete?: () => void
}

export function Confetti({
  particleCount = 200,
  fireOnMount = true,
  onComplete,
}: ConfettiProps) {
  const hasFireRef = useRef(false)

  useEffect(() => {
    if (!fireOnMount || hasFireRef.current) return
    hasFireRef.current = true

    fireConfetti(particleCount)

    // Confetti animation takes about 3-4 seconds to complete
    const timer = setTimeout(() => {
      onComplete?.()
    }, 4000)

    return () => clearTimeout(timer)
  }, [fireOnMount, particleCount, onComplete])

  return null
}

/**
 * Fire confetti with optimized settings for celebration effects
 */
export function fireConfetti(count = 200) {
  const defaults = {
    origin: { y: 0.7 },
  }

  function fire(particleRatio: number, opts: confetti.Options) {
    confetti({
      ...defaults,
      ...opts,
      particleCount: Math.floor(count * particleRatio),
    })
  }

  fire(0.25, {
    spread: 26,
    startVelocity: 55,
  })

  fire(0.2, {
    spread: 60,
  })

  fire(0.35, {
    spread: 100,
    decay: 0.91,
    scalar: 0.8,
  })

  fire(0.1, {
    spread: 120,
    startVelocity: 25,
    decay: 0.92,
    scalar: 1.2,
  })

  fire(0.1, {
    spread: 120,
    startVelocity: 45,
  })
}

/**
 * Hook to fire confetti programmatically
 */
export function useConfetti() {
  return {
    fire: fireConfetti,
  }
}
